/* tslint:disable:no-any */
import {
  Attribute,
  BelongsTo,
  JsonApiModel,
  JsonApiModelConfig,
} from '@elearnio/angular2-jsonapi';
import { Injector } from '@angular/core';

import { H5pRunnerTranslationsService } from '@shared/h5p-translations/h5p-runner-translations.service';
import { H5PEvent } from '@core/constants/h5p-events.constant';

import { CourseActivitySection } from './course-activity-section.model';
import { type2img } from '../../core/helper/h5p-icons-helper';

/** Nullsafe key read of an unknown, non-array container. */
const readKey = (container: unknown, key: string): unknown =>
  typeof container === 'object' && container !== null
    ? (container as Record<string, unknown>)[key]
    : undefined;

@JsonApiModelConfig({
  type: 'course_activities',
})
export class CourseActivity extends JsonApiModel {
  @Attribute({ serializedName: 'course_activity_section_id' })
  courseActivitySectionId!: number;

  @Attribute({ serializedName: 'activity_config' })
  activityConfig!: any;

  @Attribute({ serializedName: 'content_json' })
  contentJSON!: string;

  @Attribute({ serializedName: 'h5p_library' })
  h5pLibrary!: string;

  @Attribute({ serializedName: 'content_id' })
  contentId!: string;

  @Attribute({ serializedName: 'disabled' })
  disabled!: boolean;

  @Attribute()
  optional!: boolean;

  @Attribute()
  order!: number;

  @Attribute({ serializedName: 'activity_done' })
  activityDone: boolean | undefined;

  @Attribute({ serializedName: 'learning_pause_waiting_days', readonly: true })
  learningPauseWaitingDays!: number | null;

  @BelongsTo()
  courseActivitySection!: CourseActivitySection;

  private activityIcon = '';
  private _cachedContent: any = null;
  private _lastContentJSON = '';
  private _cachedTitlePath: string[] | null = null;
  private _lastH5pLibrary = '';
  private static injector: Injector | undefined;

  private titlesInLibraries: { [key: string]: string } = {
    'H5P.InteractiveVideo 1.21': 'interactiveVideo/video',
    'H5P.Audio 1.4': 'wizard/audioWrapper',
    'H5P.ContentPage 1.0': 'wizard/introPage',
    'H5P.Checklist 1.0': 'wizard/introPage',
    'H5P.Slides 1.0': 'presentation/introPage',
    'H5P.ImageHotspots 1.8': 'imageHotspots/hotspotsInfo',
    'H5P.File 1.0': 'wizard/fileWrapper',
    'H5P.Feedback 1.0': 'wizard/introPage',
    'H5P.Flashcards 1.5': 'wizard/basics',
    'H5P.Link 1.3': 'wizard/linkWrapper',
    'H5P.QuestionSet 1.17': 'wizard/introPage',
    'H5P.MemoryGame 1.3': 'wizard/basics',
    'H5P.Blanks 1.12': 'blanks/basics',
    'H5P.DragText 1.8': 'dragWords/basics',
    'H5P.Essay 1.2': 'wizard/introPageTab',
    'H5P.DragQuestion 1.14': 'question/basics',
    'H5P.ImageHotspotQuestion 1.8':
      'imageHotspotQuestion/backgroundImageSettings',
    'H5P.Dialogcards 1.8': 'wizard/basics',
    'H5P.Timeline 1.1': 'timeline/introPage',
    'H5P.MediaTask 1.0': 'wizard/basics',
    'H5P.Scorm 1.0': 'wizard/introPage',
    'H5P.ImageSlider 1.0': 'wizard/basics',
    'H5P.Task 1.0': 'wizard/introPage',
    'H5P.LTI 1.0': 'wizard/introPage',
    'H5P.SortParagraphs 0.12': 'wizard/basics',
    'H5P.LearningPause 1.0': 'wizard/introPage',
  };

  public get description(): string {
    const titlePath = this.getTitlePath();
    if (!titlePath) {
      return '';
    }
    const [wizard, tab1] = titlePath;
    const content = this.content;
    if (content[wizard]?.[tab1]) {
      if (content[wizard][tab1]['taskDescription']) {
        return content[wizard][tab1]['taskDescription'];
      }
      return content[wizard][tab1]['description'] || '';
    }
    return '';
  }

  public get icon(): string {
    this.activityIcon = type2img(this.h5pLibrary.split(' ')[0]);
    return this.activityIcon;
  }

  public get name(): string {
    return this.title;
  }

  private getTitlePath(): string[] | null {
    if (this._lastH5pLibrary !== this.h5pLibrary) {
      if (!this.h5pLibrary || !this.titlesInLibraries[this.h5pLibrary]) {
        // Try to find a match by library name without exact version
        if (this.h5pLibrary) {
          const libraryName = this.h5pLibrary.split(' ')[0];
          const matchingKey = Object.keys(this.titlesInLibraries).find(key =>
            key.startsWith(libraryName + ' '),
          );
          if (matchingKey) {
            this._cachedTitlePath =
              this.titlesInLibraries[matchingKey].split('/');
          } else {
            this._cachedTitlePath = null;
          }
        } else {
          this._cachedTitlePath = null;
        }
      } else {
        this._cachedTitlePath =
          this.titlesInLibraries[this.h5pLibrary].split('/');
      }
      this._lastH5pLibrary = this.h5pLibrary;
    }
    return this._cachedTitlePath;
  }

  public get title(): string {
    return this.titleIn(this.content);
  }

  /**
   * Title read out of any content object of this activity — the source content
   * or a translation-merged copy of it; empty when the path carries no title.
   */
  public titleIn(content: unknown): string {
    const titlePath = this.getTitlePath();
    if (!titlePath) {
      return '';
    }
    const [wizard, tab1] = titlePath;
    const title = readKey(readKey(readKey(content, wizard), tab1), 'title');
    return typeof title === 'string' ? title : '';
  }

  /**
   * Sync read of the member-language title from the per-course translation cache
   * (callers must have preloaded it); falls back to the source title.
   */
  public getTranslatedTitle(userLanguage: string): string {
    const source = this.title;
    const titlePath = this.getTitlePath();
    if (!titlePath || !userLanguage || !CourseActivity.injector) {
      return source;
    }
    try {
      const map = CourseActivity.injector
        .get(H5pRunnerTranslationsService)
        .mapFor(this.id, userLanguage);
      const value = map?.[`${titlePath[0]}/${titlePath[1]}:title`];
      return typeof value === 'string' && value.trim() !== '' ? value : source;
    } catch {
      return source;
    }
  }

  public set title(newTitle: string) {
    const titlePath = this.getTitlePath();
    if (!titlePath) {
      return;
    }
    const [wizard, tab1] = titlePath;
    const parsedContent = this.content;
    if (parsedContent[wizard] && parsedContent[wizard][tab1]) {
      parsedContent[wizard][tab1]['title'] = newTitle;
      this.contentJSON = JSON.stringify(parsedContent);
    }
  }

  public get content(): any {
    if (this._lastContentJSON !== this.contentJSON) {
      try {
        this._cachedContent = JSON.parse(this.contentJSON);
        this._lastContentJSON = this.contentJSON;
      } catch {
        this._cachedContent = {};
        this._lastContentJSON = this.contentJSON;
      }
    }
    return this._cachedContent;
  }

  /**
   * Set the injector to allow service access from model
   */
  static setInjector(injector: Injector | undefined): void {
    CourseActivity.injector = injector;
  }

  /**
   * Merges the member's language translations into content, per-field falling back to source.
   * No translation row or any failure resolves to the untouched `this.content`.
   */
  /** A Quiz that a person must review before the learner gets a result. */
  public get requiresManualReview(): boolean {
    return (
      this.h5pLibrary === H5PEvent.VERSION.QUIZ &&
      readKey(
        readKey(readKey(this.content, 'wizard'), 'behaviourTab'),
        'requiresManualReview',
      ) === true
    );
  }

  public getTranslatedContent(userLanguage: string): Promise<any> {
    const source = this.content;
    if (!CourseActivity.injector) {
      return Promise.resolve(source);
    }
    try {
      return CourseActivity.injector
        .get(H5pRunnerTranslationsService)
        .translateContent(this.id, this.h5pLibrary, source, userLanguage);
    } catch {
      return Promise.resolve(source);
    }
  }
}
